import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, FolderOpen, CreditCard, LogOut, HelpCircle, MessageSquarePlus } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';

interface MobileDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  userInfo?: {
    email: string;
    first_name: string;
    surname: string;
    company_name?: string;
    user_type?: string;
  } | null;
}

const MobileDrawer: React.FC<MobileDrawerProps> = ({ isOpen, onClose, userInfo }) => {
  const navigate = useNavigate();
  const [showHelp, setShowHelp] = useState(false);

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userType');
    onClose();
    navigate('/');
  };

  const handleNavigate = (path: string) => {
    onClose();
    navigate(path);
  };

  const handleHelpClick = () => {
    onClose();
    setShowHelp(true);
  };

  const menuItems = [
    { icon: <FolderOpen size={20} />, label: 'My Campaigns', path: '/brand/campaign' },
    { icon: <CreditCard size={20} />, label: 'Subscription', path: '/subscription' },
    { divider: true },
    { icon: <MessageSquarePlus size={20} />, label: 'Feature Request', action: () => { onClose(); window.open('mailto:info@brandcomply.ai?subject=Feature%20Request', '_blank'); } },
    { icon: <HelpCircle size={20} />, label: 'Help & Support', action: handleHelpClick },
  ];

  return (
    <>
      {/* Overlay */}
      <div 
        className={`mobile-drawer-overlay ${isOpen ? 'open' : ''}`}
        onClick={onClose}
      />

      {/* Drawer */}
      <div className={`mobile-drawer ${isOpen ? 'open' : ''}`}>
        {/* Header */}
        <div className="mobile-drawer-header">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <img 
                src="/brandcomply_logo_full.png" 
                alt="BrandComply" 
                style={{ height: 32, marginBottom: 16 }}
              />
              {userInfo && (
                <>
                  <div style={{ fontWeight: 600, fontSize: 16 }}>
                    {userInfo.first_name} {userInfo.surname}
                  </div>
                  <div style={{ fontSize: 14, opacity: 0.8, marginTop: 4 }}>
                    {userInfo.email}
                  </div>
                  {userInfo.company_name && (
                    <div style={{ fontSize: 13, opacity: 0.7, marginTop: 4 }}>
                      {userInfo.company_name}
                    </div>
                  )}
                </>
              )}
            </div>
            <button 
              onClick={onClose}
              style={{ 
                background: 'rgba(255,255,255,0.1)', 
                border: 'none', 
                borderRadius: 8,
                padding: 8,
                color: '#fff',
                cursor: 'pointer'
              }}
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Menu Items */}
        <div className="mobile-drawer-content">
          {menuItems.map((item, index) => 
            item.divider ? (
              <div 
                key={index} 
                style={{ height: 1, background: '#e0e0e0', margin: '8px 0' }} 
              />
            ) : (
              <button
                key={item.path || item.label}
                className="mobile-drawer-item"
                onClick={() => item.action ? item.action() : handleNavigate(item.path!)}
                style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer' }}
              >
                {item.icon}
                <span>{item.label}</span>
              </button>
            )
          )}
        </div>

        {/* Footer - Logout */}
        <div style={{ padding: 16, borderTop: '1px solid #e0e0e0' }}>
          <button
            onClick={handleLogout}
            className="mobile-btn mobile-btn-outline mobile-btn-full"
            style={{ color: '#dc2626', borderColor: '#dc2626' }}
          >
            <LogOut size={18} style={{ marginRight: 8 }} />
            Log Out
          </button>
        </div>
      </div>

      {/* Help Modal */}
      <Dialog open={showHelp} onOpenChange={setShowHelp}>
        <DialogContent style={{ maxWidth: '90vw', maxHeight: '80vh', overflowY: 'auto' }}>
          <DialogTitle>Help & Support</DialogTitle>
          <div style={{ padding: '16px 0' }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>Getting Started</h3>
            <p style={{ fontSize: 14, marginBottom: 16, lineHeight: 1.6 }}>
              Welcome to BrandComply! Here's how to get the most out of our platform:
            </p>

            <h4 style={{ fontSize: 15, fontWeight: 600, marginBottom: 8, marginTop: 16 }}>Creating Your First Campaign</h4>
            <ol style={{ fontSize: 14, lineHeight: 1.8, paddingLeft: 20, marginBottom: 16 }}>
              <li>1. Tap "Create Campaign" on the home screen</li>
              <li>2. Upload your brand brief as PDF or Word file or enter it manually</li>
              <li>3. We structure the brief automatically and you can edit it until you are happy</li>
              <li>5. We generate a submission link that is connected to the brief</li>
              <li>6. Share the submission link with creators</li>
              <li>7. Creators upload their videos via the submission link and follow a simple workflow to get objective and private feedback on how their video meets the brief.</li>
              <li>8. You review the submissions in your dashboard and see, via the BrandComply score, how well each video matches the brief.</li>
            </ol>

            <h4 style={{ fontSize: 15, fontWeight: 600, marginBottom: 8, marginTop: 16 }}>Managing Submissions</h4>
            <ul style={{ fontSize: 14, lineHeight: 1.8, paddingLeft: 20, marginBottom: 16 }}>
              <li>1. View all submissions for each campaign</li>
              <li>2. Check BrandComply scores and detailed analysis</li>
              <li>3. Request revisions with specific feedback</li>
              <li>4. Approve videos that meet your standards</li>
            </ul>

            <h4 style={{ fontSize: 15, fontWeight: 600, marginBottom: 8, marginTop: 16 }}>Need More Help?</h4>
            <p style={{ fontSize: 14, marginBottom: 8, lineHeight: 1.6 }}>
              For additional support or questions, please contact us at:
            </p>
            <p style={{ fontSize: 14, fontWeight: 600, color: '#B1FA63' }}>
              info@brandcomply.ai
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MobileDrawer;
